import { PublicClientApplication } from '@azure/msal-browser'
import { MsalProvider } from '@azure/msal-react'
import React, { useEffect, useState } from 'react'
import { createRoot } from 'react-dom/client'
import { BrowserRouter } from 'react-router-dom'
import { createGlobalStyle, ThemeProvider } from 'styled-components'

import App from './App'
import { MsalContextProvider, useMsalContext } from './application/context/msalContext'
import { authTypeGuard } from './domain/utils/guards'
import { initializeMsal, loginRequestExt, loginRequestInt } from './infrastructure/authbymsal'
import { AUTH_TYPE } from './infrastructure/enums/authentication.enum'
import { useWindowSize } from './infrastructure/hooks/useResponsiveHelper'
import theme from './infrastructure/themes/theme'
import setupI18n from './infrastructure/translation/setupI18n'
import LoginPage from './ui/modules/login/login'
import setupIconLibrary from './utils/setupIconLibrary'

const AuthSelector = () => {
  const [msalInstance, setMsalInstance] = useState<PublicClientApplication | null>(null)
  const typeLocalStorage = localStorage.getItem('selectedType') as AUTH_TYPE | null
  const authType = authTypeGuard(typeLocalStorage) ? typeLocalStorage : null
  const [selectedType, setSelectedType] = useState<AUTH_TYPE | null>(authType)

  const { handleLoginRequestChange } = useMsalContext()

  useEffect(() => {
    if (!selectedType) {
      return
    }
    const initMsal = async () => {
      const instance = await initializeMsal(selectedType)
      await instance.initialize()
      setMsalInstance(instance)
    }
    if (selectedType && !msalInstance) {
      handleLoginRequestChange(
        selectedType === AUTH_TYPE.EXTERNAL ? loginRequestExt : loginRequestInt
      )
      initMsal()
    }
  }, [selectedType])

  const GlobalStyle = createGlobalStyle`
* {
  margin: 0;
  padding: 0;
  box-sizing: border-box;
}

html, body, #root {
  display: flex;
  width: 100%;
  min-height: 100%;
  overflow: hidden;
}

body {
  margin: 0;
  background-color: #36404B;
  font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', 'Roboto', 'Oxygen', 'Ubuntu', 'Cantarell', 'Fira Sans', 'Droid Sans', 'Helvetica Neue', sans-serif;
  -webkit-font-smoothing: antialiased;
  -moz-osx-font-smoothing: grayscale;
}

code {
  font-family: source-code-pro, Menlo, Monaco, Consolas, 'Courier New', monospace;
}
`

  const ResponsiveThemeProvider = ({ children }: { children: React.ReactNode }) => {
    const [width] = useWindowSize()

    const themeKey = width < 800 ? 'mobile' : 'desktop'

    const themes = {
      desktop: theme,
      mobile: { ...theme, isMobile: true },
    }

    return <ThemeProvider theme={themes[themeKey]}>{children}</ThemeProvider>
  }

  return (
    <BrowserRouter>
      <ResponsiveThemeProvider>
        <GlobalStyle />
        {!msalInstance || !selectedType ? (
          <LoginPage handleSelectAuthType={setSelectedType} />
        ) : (
          <MsalProvider instance={msalInstance}>
            <App selectedType={selectedType} />
          </MsalProvider>
        )}
      </ResponsiveThemeProvider>
    </BrowserRouter>
  )
}

setupI18n()
setupIconLibrary()

const container = document.getElementById('root')
const root = createRoot(container!) // use createRoot(container!) for TypeScript
root.render(
  <React.StrictMode>
    <MsalContextProvider>
      <AuthSelector />
    </MsalContextProvider>
  </React.StrictMode>
)
